import React, { useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  ErrorFallback,
  MessageAlert,
  Loader,
  DataTableHoc,
  buildNotification,
  GenerateNotification,
} from 'smart-react';
import { useLocation, useParams } from 'react-router-dom';
import {
  ERROR_MESSAGE,
  ROLLOUTS,
} from '../../../constants/applicationConstants';
import { GridColumnsList } from '../Components/Filter/FilterElements';
import DataColumns from '../Components/DataColumns/DataColumns';
import { Card } from '@progress/kendo-react-layout';

import './RolloutList.scss';

import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
  deleteRolloutData,
  downloadRolloutArchives,
  getRolloutList,
} from '../Services/SearchFilesService';

import {
  ROW_LAYOUTS,
  PAGE_SIZE,
} from '../../../constants/applicationConstants';

import {
  deleteFilter,
  applyFilter,
  saveFilters,
} from '../../../Utils/Filters/filterSliderHandler';
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES,
} from '../../../constants/eventDataTypes';

const BaseRolloutDataTable = React.lazy(
  () => import('../Components/DataTable/DataTable')
);
const RolloutDataTable = DataTableHoc(BaseRolloutDataTable);

/**
 * Rollout main screen
 * @type {React.FC<Props>}
 * @returns {React.ReactElement} The Rollout component.
 */
const RolloutList = () => {
  const { projectId, versionId } = useParams();
  const { state } = useLocation();
  const [rolloutsData, setRolloutsData] = React.useState([]);
  const dataColumnsArray = DataColumns({});

  const { downloadRolloutStatus, rolloutPlatform, downloadRolloutName } =
    state || {};
  const [visible, setVisible] = React.useState(downloadRolloutStatus);
  const [deleteDialogVisible, setDeleteDialogVisible] = React.useState(false);
  const [showBugsModal, setShowBugsModal] = React.useState(false);
  const [loading, setLoading] = useState(false);
  const [dialogueLoading, setDialogueLoading] = useState(false);
  const [rolloutName, setRolloutName] = useState(null);
  const [search, setSearch] = useState('');
  const [alertMessage, setAlertMessage] = React.useState({
    alert: false,
    message: null,
  });
  // used to check API is ready to call
  const [isReady, setIsReady] = React.useState(false);

  const toggleDialog = () => {
    setVisible(!visible);
  };

  const toggleDeleteDialog = () => {
    setDeleteDialogVisible(!deleteDialogVisible);
  };

 

  /**
   * Description: function for download rollout
   * @return
   * @param platformType
   * @param rolloutName
   * @param dialogueLoading
   */
  const downloadRolloutArchive = async (
    platformType,
    rolloutName,
    dialogueLoading = true
  ) => {
    try {
      if (dialogueLoading) {
        setDialogueLoading(true);
      } else {
        setLoading(true);
      }
      const response = await downloadRolloutArchives(platformType, rolloutName);
      if (response.isSuccess) {
        window.location.replace(response.payload?.downloadUrl);
      } else {
        GenerateNotification(
          buildNotification(ERROR_MESSAGE),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
        );
      }
      setDialogueLoading(false);
    } catch (error) {
      GenerateNotification(
        buildNotification(ERROR_MESSAGE),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
      );
    } finally {
      setDialogueLoading(false);
      setVisible(false);
      setLoading(false);
    }
  };

  /**
   * Description: function for display Delete Dialog and set rolloutName state
   * @param rolloutTempName string
   * @return
   */
  const rolloutDelete = async (rolloutTempName) => {
    toggleDeleteDialog();
    setRolloutName(rolloutTempName);
  };

  /**
   * delete the Rollout
   */
  const onDelete = async () => {
    setLoading(true);
    toggleDeleteDialog();
    const response = await deleteRolloutData(rolloutName);
    if (response?.isSuccess) {
      setAlertMessage({
        alert: true,
        message: `The Rollout ${rolloutName} has been Deleted!`,
      });
      const rolloutDataTemp = rolloutsData.filter(
        (obj) => obj?.rollout_name !== rolloutName
      );
      setRolloutsData([...rolloutDataTemp]);
      await fetchRolloutFiles();
    } else {
      setAlertMessage({
        alert: true,
        message: 'There is some issue with!',
      });
    }
    setLoading(false);
  };

  const fetchRolloutFiles = async () => {
    const response = await getRolloutList({
      limit: `${process.env.DefaultLimit}`,
      searchValue: '',
      offset: 0,
      projectId,
      versionId,
    });
    setRolloutsData(response.rollouts);
  };

  /**
   * Description: function for showing bug information modal
   * @return
   * @param rolloutTempName
   */
  const showBugs = async (rolloutTempName) => {
    setRolloutName(rolloutTempName);
    setShowBugsModal(true);
  };

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <React.Suspense fallback={<Loader />}>
        {loading && <Loader />}
        {alertMessage?.alert && (
          <MessageAlert
            type={'danger'}
            message={alertMessage.message}
            isIcon={true}
            iconName={'error'}
          />
        )}
        {visible && (
          <Dialog
            title={'Download Rollout'}
            onClose={toggleDialog}
            className="search-dialog"
          >
            <h5 className="text-center ">
              The Rollout has been created successfully!
            </h5>
            <h6>
              You can download it from{' '}
              <a
                className="text-primary"
                onClick={(e) =>
                  downloadRolloutArchive(rolloutPlatform, downloadRolloutName)
                }
              >
                Here
              </a>
            </h6>
            {dialogueLoading && <Loader />}
          </Dialog>
        )}
        {deleteDialogVisible && (
          <Dialog title={'Please confirm'} onClose={toggleDeleteDialog}>
            <p>Are you sure you want to delete this rollout?</p>
            <DialogActionsBar>
              <button
                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                onClick={() => (setRolloutName(null), toggleDeleteDialog())}
              >
                No
              </button>
              <button
                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                onClick={onDelete}
              >
                Yes
              </button>
            </DialogActionsBar>
          </Dialog>
        )}
       
        <div className="container mx-auto account-form-container">
          <div className="add-test add-test-page cards-layout-page container mx-auto">
            <div className="card-detail-content">
              <Card className="seprate-card">
                <div className="card-content">
                  <RolloutDataTable
                    deleteFilter={deleteFilter}
                    applyFilter={applyFilter}
                    saveFilters={saveFilters}
                    moduleName={ROLLOUTS}
                    dataColumns={dataColumnsArray}
                    gridColumnsList={GridColumnsList}
                    dataTableName={ROLLOUTS}
                    setIsReady={setIsReady}
                    isReady={isReady}
                    rowLayoutConfigName={`${ROLLOUTS}.DataGrid.RowLayout`}
                    pageLengthConfigName={`${ROLLOUTS}.DataGrid.ItemsPerPage`}
                    dataSet={rolloutsData}
                    setDataSet={setRolloutsData}
                    initialSort={{
                      field: 'rollout_name',
                      dir: 'asc',
                    }}
                    isCardonMobileView={false}
                    total={process.env.DefaultLimit}
                    defaultRowLayout={process.env.RowLayout}
                    rowLayouts={ROW_LAYOUTS}
                    pageSizes={PAGE_SIZE}
                    projectId={projectId}
                    versionId={versionId}
                    search={search}
                    setSearch={setSearch}
                    isSelectable={true}
                    rolloutDelete={rolloutDelete}
                    rolloutName={rolloutName}
                    setShowBugsModal={setShowBugsModal}
                    showBugsModal={showBugsModal}
                    dataColumnsParams={{
                      projectId,
                      versionId,
                      downloadRolloutArchive,
                      showBugs,
                    }}
                  />
                </div>
              </Card>
            </div>
          </div>
        </div>
      </React.Suspense>
    </ErrorBoundary>
  );
};
export default RolloutList;
